/**
 * 지점 포털 - 주문 상세 패널
 *
 * 본점 console/pages/transaction/-components/TransactionDetail.tsx 를 그대로 가져왔다.
 * import 경로만 바꿨고 화면 구성은 손대지 않았다.
 *
 * 주의: 지점계정(LV6)에서 403 이 나는 API 가 다수 있다.
 *   /v1/common/*        (배송업체·발신자·경조사어·배송메모·경조내용·취소사유)
 *   /order/product, /order/branch, /order/branch/option, /order/customer,
 *   /order/:idx/manager, /order/:idx/chat, /console/management/*
 *   주문수정(PUT /order/:idx), 복사, 취소, 삭제
 * 해당 영역은 값이 비거나 저장 시 실패한다. 정리 범위가 정해지면 걷어낸다.
 */
import Button from "../../common/components/button";
import TableName from "../../console/components/common/tableName";
import SelectBox from "../../console/components/common/selectBox";
import { ReactComponent as CopyIcon } from "../../console/assets/icons/copy.svg";
import { ReactComponent as RadioIcon } from "../../console/assets/icons/radioIcon.svg";
import { ReactComponent as DownArrow } from "../../console/assets/icons/downArrow.svg";
import { ReactComponent as Select } from "../../console/assets/icons/select.svg";
import { ReactComponent as UploadImage } from "../../console/assets/icons/uploadImage.svg";
import { ReactComponent as UploadOnImage } from "../../console/assets/icons/uploadOnImage.svg";
import { ReactComponent as CloseIcon } from "../../console/assets/icons/closeWhiteIcon.svg";
import { ReactComponent as PlusIcon } from "../../console/assets/icons/plusIcon.svg";
import { ReactComponent as CloseBlackIcon } from "../../console/assets/icons/closeIcon.svg";
import { ReactComponent as LeftArrow } from "../../console/assets/icons/leftArrow.svg";
import { ReactComponent as FileSearch } from "../../console/assets/icons/fileSearch.svg";
import { ReactComponent as RightArrow } from "../../console/assets/icons/rightArrow.svg";
import React, { useEffect, useRef, useState } from "react";
import Input from "../../console/components/common/input";
import Toggle from "../../console/components/common/toggle";
import DayPickerInput from "../../console/components/common/DayPickerInput";
import { useGetOrderInfo } from "../../common/hooks/console/transaction/useGetOrderInfo";
import { api_uri } from "../../common/hooks/utils/enum";
import { BRANCH_REQUEST_DIRECT_INPUT, BRANCH_REQUEST_OPTIONS, isBranchRequestPreset } from "../../common/constants/branchRequest";
import useToastStore from "../../console/store/useToastStore";
import { usePutOrderStatus } from "../../common/hooks/console/transaction/usePutOrderStatus";
import { useSystemPopStore } from "../../console/store/useSystemPopStore";
import { usePostOrderPhotoCheck } from "../../common/hooks/console/transaction/usePostOrderPhotoCheck";
import { useGetOrderProduct } from "../../common/hooks/console/transaction/useGetOrderProduct";
import { useGetOrderBranch } from "../../common/hooks/console/transaction/useGetOrderBranch";
// import SearchAddress from "../../console/components/common/searchAddress";
import { useGetCommonSender } from "../../common/hooks/common/useGetCommonSender";
import { useGetCommonWords } from "../../common/hooks/common/useGetCommonWords";
import { useGetCommonMemo } from "../../common/hooks/common/useGetCommonMemo";
import { useGetCommonEvent } from "../../common/hooks/common/useGetCommonEvent";
import { usePutBranchDelete } from "../../common/hooks/console/transaction/usePutBranchDelete";
import { usePutOrderInfo } from "../../common/hooks/console/transaction/usePutOrderInfo";
import { useGetCommonCancel } from "../../common/hooks/common/useGetCommonCancel";
import { useAuthStore } from "../../console/store/useAuthStore";
import { usePutReceipt } from "../../common/hooks/console/transaction/usePutReceipt";
import { useGetOrderBranchOption } from "../../common/hooks/console/transaction/useGetOrderBranchOption";
import loginLogo from "../../console/assets/images/receiptLogo.png";
import { useDeleteOrder } from "../../common/hooks/console/transaction/useDeleteOrder";
import Product from "../../console/pages/transaction/-components/Product";
import printJS from "print-js";
import logo from "../../common/assets/images/logo.png";
import CheckBox from "../../console/components/common/checkBox";
import { useGetOrderManager } from "../../common/hooks/console/transaction/useGetOrderManager";
import { useGetCustomerInfo } from "../../common/hooks/console/management/customer/useGetCustomerInfo";
import NewOrderSearchAddress from "console/components/common/newOrderSearchAddress";
import { makeIntComma } from "common/hooks/utils/utils";

const hoursOptions = [
    { value: "오전중", label: "오전중" },
    { value: "오후중", label: "오후중" },
    { value: "당일배송", label: "당일배송" },
    { value: "08", label: "오전 08시" },
    { value: "09", label: "오전 09시" },
    { value: "10", label: "오전 10시" },
    { value: "11", label: "오전 11시" },
    { value: "12", label: "오후 12시" },
    { value: "13", label: "오후 01시" },
    { value: "14", label: "오후 02시" },
    { value: "15", label: "오후 03시" },
    { value: "16", label: "오후 04시" },
    { value: "17", label: "오후 05시" },
    { value: "18", label: "오후 06시" },
    { value: "19", label: "오후 07시" },
    { value: "20", label: "오후 08시" },
];
const minutesOptions = [
    { value: "00", label: "00분" },
    { value: "10", label: "10분" },
    { value: "20", label: "20분" },
    { value: "30", label: "30분" },
    { value: "40", label: "40분" },
    { value: "50", label: "50분" },
];
const conditionOptions = [
    { value: "까지", label: "까지" },
    { value: "예식", label: "예식" },
    { value: "행사", label: "행사" },
    { value: "도착", label: "도착" },
];
type ImageItem = {
    url: File | string;
    file_idx?: number;
};
const BranchOrderDetail = ({
    selectedIdx,
    onOrderNumberChange,
    onRefresh,
    onDeleted,
}: {
    selectedIdx: number;
    onOrderNumberChange: (orderNumber: string) => void;
    onRefresh?: (refetchFn: () => Promise<any>) => void;
    onDeleted?: () => void;
}) => {
    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();
    const name = useAuthStore(state => state.m_name);
    const level = useAuthStore(state => state.m_level);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const requestRef = useRef<HTMLTextAreaElement | null>(null);
    const customerRequestRef = useRef<HTMLTextAreaElement | null>(null);

    const [isCustomInput, setIsCustomInput] = useState(false);
    const [selectedProductIdx, setSelectedProductIdx] = useState<number[]>([]);
    const [selectedOption, setSelectedOption] = useState<string>("직접입력");
    const [request, setRequest] = useState<string>("");
    const [imagePopup, setImagePopup] = useState<string | null>(null);
    const [selectedBranch, setSelectedBranch] = useState<string>("");
    const [orderStatus, setOrderStatus] = useState<number>(0);
    const [orderName, setOrderName] = useState<string>("");
    const [orderMobile, setOrderMobile] = useState<string>("");
    const [salesAmount, setSalesAmount] = useState<number>(0);
    const [customerRequest, setCustomerRequest] = useState<string>("");
    const [productQuantity, setProductQuantity] = useState<number>(0);
    const [orderAmount, setOrderAmount] = useState<number>(0);
    const [productImages, setProductImages] = useState<ImageItem[]>([]);
    const [readyImages, setReadyImages] = useState<(File | string)[]>([]);
    const [doneImages, setDoneImages] = useState<(File | string)[]>([]);
    const [tempReadyImages, setTempReadyImages] = useState<(File | string)[]>([]);
    const [tempDoneImages, setTempDoneImages] = useState<(File | string)[]>([]);
    const [tempFileImages, setTempFileImages] = useState<(File | string)[]>([]);
    const [tempProductImages, setTempProductImages] = useState<ImageItem[]>([]);
    const [tempProductImageIndexes, setTempProductImageIndexes] = useState<number[]>([]);
    const [productUploadPop, setProductUploadPop] = useState<boolean>(false);
    const [readyUploadPop, setReadyUploadPop] = useState(false);
    const [doneUploadPop, setDoneUploadPop] = useState(false);
    const [delFileIdx, setDelFileIdx] = useState<number[]>([]);
    const [receiver, setReceiver] = useState<string>("");
    const [receiverMobile, setReceiverMobile] = useState<string>("");
    const [related, setRelated] = useState<string>("");
    const [depart, setDepart] = useState<string>("");
    const [companyTel, setCompanyTel] = useState<string>("");
    const [companyAddress, setCompanyAddress] = useState<string>("");
    const [deliveryHour, setDeliveryHour] = useState<string>("");
    const [deliveryMinute, setDeliveryMinute] = useState<string>("");
    const [selectedProduct, setSelectedProduct] = useState({
        product_idx: 0,
        product_image: "",
        product_code: "",
        product_code_name: "",
        product_union: false,
        product_category_idx: 0,
        product_category: "",
        product_name: "",
        product_amount: 0,
    });
    const [currentPage, setCurrentPage] = useState<number | null>(1);
    const [search, setSearch] = useState<string>("category_name");
    const [searchTxt, setSearchTxt] = useState<string>("");
    const [inputValue, setInputValue] = useState<string>(currentPage?.toString() || "");
    const [branchTxt, setBranchTxt] = useState<string>("");
    const [branchLabel, setBranchLabel] = useState<string>("");
    const [branchOption, setBranchOption] = useState<number>(0);
    const [customerIdx, setCustomerIdx] = useState<number | string>("");
    const [union, setUnion] = useState<boolean>(false);
    const [immediate, setImmediate] = useState<boolean>();
    const [zoneCode, setZoneCode] = useState<string>("");
    const [deliveryCondition, setDeliveryCondition] = useState<string>("");
    const [address, setAddress] = useState<string>("");
    const [detailAddress, setDetailAddress] = useState<string>("");
    const [fileMemo, setFileMemo] = useState<string>("");
    const [senderTxt, setSenderTxt] = useState<string>("");
    const [sender, setSender] = useState<number | string>(0);
    const [customSender, setCustomSender] = useState<string>("");
    const [word, setWord] = useState<number | string>(0);
    const [customWord, setCustomWord] = useState<string>("");
    const [wordTxt, setWordTxt] = useState<string>("");
    const [memo, setMemo] = useState<number | string>(0);
    const [customMemo, setCustomMemo] = useState<string>("");
    const [event, setEvent] = useState<number | string>(0);
    const [event2, setEvent2] = useState<number | string>(0);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>();
    const [cancel, setCancel] = useState<number | string>(0);
    const [cancelDetail, setCancelDetail] = useState<string>("");
    const [files, setFiles] = useState<(File | string)[]>([]);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [receiverError, setReceiverError] = useState<boolean>(false);
    const [receiverMobileError, setReceiverMobileError] = useState<boolean>(false);
    const [timeError, setTimeError] = useState<boolean>(false);
    const [initialData, setInitialData] = useState<any>(null);
    const [branchError, setBranchError] = useState<boolean>(false);
    const [branchOptionError, setBranchOptionError] = useState<boolean>(false);
    const [orderMemo, setOrderMemo] = useState<string>("");
    const [isStatusOpen, setIsStatusOpen] = useState(true);
    const [isProductOpen, setIsProductOpen] = useState(true);
    const [isReceiverOpen, setIsReceiverOpen] = useState(true);
    const [isOrderOpen, setIsOrderOpen] = useState(true);
    const [special, setSpecial] = useState<boolean>(false);
    const [branchOptionLabel, setBranchOptionLabel] = useState<string>("");
    const [uploadPop, setUploadPop] = useState(false);
    const [eventContent, setEventContent] = useState<string>("");
    const [branchProductPop, setBranchProductPop] = useState<boolean>(false);
    const [checkedManager, setCheckedManager] = useState<{ [key: string]: boolean }>({});
    const [initialCheckedManager, setInitialCheckedManager] = useState<{ [key: string]: boolean }>({});
    const [customerName, setCustomerName] = useState<string>("");
    const textOnlyHourOptions = ["오전중", "오후중", "당일배송"];
    const isTextOnlyHour = textOnlyHourOptions.includes(deliveryHour);

    const changedManagerIdxList = Object.entries(checkedManager)
        .filter(([_, isChecked]) => isChecked)
        .map(([key]) => Number(key));

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDate = (dateStr: string) => {
        if (!dateStr) return undefined;

        const normalized = dateStr.replace(/\./g, "-");
        const [year, month, day] = normalized.split("-");
        return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const { data, refetch } = useGetOrderInfo(selectedIdx);
    const { data: cancelData } = useGetCommonCancel(true);
    const { mutate: receiptMutate } = usePutReceipt();
    const [branch, setBranch] = useState<number>(data?.status_info.customer_branch_idx || 0);
    const { data: branchData } = useGetOrderBranch(branchTxt, branch, special);
    const { data: branchOptionData } = useGetOrderBranchOption(branch);
    const { data: managerData } = useGetOrderManager(customerIdx.toString() || "");

    // 지점 삭제 - 목록의 삭제 버튼과 동일한 동작 (주문 삭제가 아니라 지점 화면에서만 숨김)
    const { mutate: branchDelete } = usePutBranchDelete(() => {
        addToast({ type: "success", message: "주문을 목록에서 삭제했습니다." });
        onDeleted?.();
    });

    const handleBranchDelete = () => {
        showPop({
            title: "이 주문을 삭제하시겠습니까?",
            content: "목록에서만 숨겨지며, 되돌리려면 본점에 요청해야 합니다.",
            rightButton: "삭제",
            type: "negative",
            onRightClick: () => {
                branchDelete(
                    { order_idx: [selectedIdx], branch_del: "Y" },
                    {
                        onError: error =>
                            addToast({
                                type: "error",
                                message: error.response?.data?.message || "삭제에 실패했습니다.",
                            }),
                    },
                );
            },
        });
    };

    const customerOptions =
        branchOptionData?.map(option => ({
            value: option.branch_idx.toString(),
            label: option.branch_option,
        })) || [];

    const handlePrintReceipt = () => {
        const getBase64Image = (imgUrl: any, callback: any) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = imgUrl;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx?.drawImage(img, 0, 0);
                const dataURL = canvas.toDataURL("image/png");
                callback(dataURL);
            };
        };
        getBase64Image(loginLogo, (base64Logo: any) => {
            receiptMutate(
                { order_idx: [selectedIdx] },
                {
                    onSuccess: data => {
                        const receiptData = data;

                        if (!receiptData) {
                            console.error("인수증 데이터가 없습니다.");
                            return;
                        }

                        // .container {
                        //     height: 88vh;
                        //     max-height: 950px;
                        // }
                        // 절단선 좌우에 같은 인수증을 한 장씩 찍는다
                        const receiptEventLabel =
                            receiptData[0].event_content?.depth1 === "직접입력"
                                ? receiptData[0].event_content?.event_content
                                    ? ` [${receiptData[0].event_content.event_content}]`
                                    : ""
                                : receiptData[0].event_content?.depth1 === "선택안함"
                                  ? ""
                                  : receiptData[0].event_content?.depth2
                                    ? ` [${receiptData[0].event_content.depth2}]`
                                    : "";
                        const receiptBody = `
                    <div class="receipt">
                    <div class="receipt-header">
                        <h1>인&nbsp;&nbsp;수&nbsp;&nbsp;증</h1>
                        <p>${receiptData[0].order_number}</p>
                    </div>

                    <table class="receipt-table">
                          <tr><td>보&nbsp;&nbsp;낸&nbsp;&nbsp;곳</td><td>${receiptData[0].wreath_sender.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td></tr>
                          <tr>
                            <td>종류및금액</td>
                            <td>
                                <div>
                                    <div class="justify-between">
                                        <div>
                                            <span>${receiptData[0].product_name}</span>
                                            <span>(${receiptData[0].product_quantity})</span>
                                        </div>
                                        <span>${receiptData[0].order_amount ? receiptData[0].order_amount / 10000 : ""}</span>
                                    </div>

                                    <div style="word-break:break-word;">${receiptData[0].customer_request !== null ? `<div>${receiptData[0].customer_request}</div>` : ""}</div>
                                </div>
                            </td>
                        </tr>
                        <tr><td>내&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;용</td><td>${receiptData[0].wreath_word}</td></tr>
                        <tr>
                          <td>받으시는 분</td>
                          <td>
                            ${receiptData[0].recipient_name}
                            ${receiptEventLabel}
                            ${receiptData[0].related_person ? ` [${receiptData[0].related_person}]` : ""}
                          </td>
                        </tr>

                        <tr><td>배&nbsp;송&nbsp;주&nbsp;소</td><td>${receiptData[0].delivery_address} <br /> ${receiptData[0].delivery_address_detail}</td></tr>
                        <tr><td>연&nbsp;&nbsp;&nbsp;락&nbsp;&nbsp;&nbsp;처</td><td>${receiptData[0].recipient_mobile}</td></tr>
                        <tr><td>배&nbsp;송&nbsp;일&nbsp;시</td><td>${receiptData[0].delivery_date} ${receiptData[0].delivery_time || "없음"}</td></tr>
                        <tr><td>배&nbsp;송&nbsp;메&nbsp;모</td><td>${receiptData[0].delivery_memo || "없음"}</td></tr>
                        <tr><td>인&nbsp;&nbsp;&nbsp;수&nbsp;&nbsp;&nbsp;자</td><td style="text-align:right;"><img src="${base64Logo}" alt="Logo" style="width:130px"; /></td></tr>
                    </table>
                </div>`;
                        const receiptHtml = `
                <html>
                    <head>
                        <title>인수증</title>
                        <style>
                        @page { size: A4 landscape; margin: 0; }
                            body {
                                font-family: Arial, sans-serif;
                                margin: 0;
                                height: 100vh;
                                display: flex;
                                flex-direction: column;
                                align-items: center;    
                                -webkit-print-color-adjust: exact; 
                                print-color-adjust: exact;    
                        }
                            .container {
                                /* border-box 가 아니면 width 98% 에 좌우 padding 이 더해져 오른쪽으로 삐져나간다 */
                                box-sizing: border-box;
                                display: flex;
                                width: 98%;
                                max-height: 95vh;
                                max-width: 1800px;
                                gap: 40px;
                                padding: 30px 20px 0 20px;
                            }
                            .receipt {
                                /* 절단선 기준 좌우 5:5. flex-basis 0 이라 내용 길이와 무관하게 반씩 나눈다 */
                                flex: 1 1 0;
                                min-width: 0;
                                background: white;
                                margin: 0;
                            }
                            .receipt {
                                border-top: 3px solid black;
                                border-left: 3px solid black;
                                border-right: 2px solid black;
                            }
                            .receipt-header {
                                padding: 20px 18px;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                border-bottom: 2px solid black;
                                background-color: #dddddd;
                                gap: 20px;
                            }
                            .receipt-header h1 {
                                font-size: 22px;
                                margin: 0;
                            }
                            .receipt-header p {
                                font-size: 20px;
                                font-weight: bold;
                                margin: 0;
                            }
                            .receipt h1 {
                                text-align: left;
                                font-size: 23px;
                            }
                            .receipt-table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-top: 0;
                            }
                            .receipt-table td {
                                padding: 8px;
                                font-size: 18px;
                                border-right: 2px solid black;
                                border-bottom: 2px solid black; 
                            }
                            .receipt-table td:first-child {
                                background-color: #F1F1F1;
                                font-weight: bold;
                                width: 25%;
                                text-align: center;
                                font-size: 15px; 
                                border-bottom: 2px solid black;
                            }
                            .receipt-table td:nth-child(2) {
                                border-left: none;
                                border-right: none;
                                font-size: 15px;
                                font-weight: normal;
                            }
                            .receipt-table tr:nth-child(1) td{
                                height: 78px; 
                            }
                            .receipt-table tr:nth-child(2) td{
                                height: 58px; 
                            }
                            .receipt-table tr:nth-child(3) td{
                                height: 63px; 
                            }
                            .receipt-table tr:nth-child(4) td{
                                height: 58px; 
                            }
                            .receipt-table tr:nth-child(5) td {
                                height: 103px; 
                            }
                            .receipt-table tr:nth-child(6) td{
                                height: 58px; 
                            }
                            .receipt-table tr:nth-child(7) td{
                                height: 58px; 
                            }
                            .receipt-table tr:nth-child(8) td{
                                height: 58px; 
                            }
                            .receipt-table tr:nth-child(9) td {
                                height: 84px;
                            }
                           .footer {
                                display: flex;
                                flex-direction: column;
                                align-items: start;
                                text-align: left;
                                font-size: 12px;
                                position: relative;
                                top: -70px; 
                                left: 310px;
                           }
                           .footer img {
                                width: 130px; 
                                height: auto;
                           }
                            .justify-between {
                                display: flex;
                                justify-content: space-between;
                                width: 100%;
                            }
                            .divider {
                                width: 1px;
                                opacity: 50%;
                                background-color: #999;
                                height: 98%;
                                align-self: center;
                            }
                        </style>
                    </head>
                    <body>
                <div class="container">
                    ${receiptBody}
                    <div class="divider"></div>
                    ${receiptBody}
                </div>
        
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(() => window.close(), 0);
                    };
                </script>
            </body>
                </html> `;

                        const printWindow = window.open("", "_blank", "width=1920,height=1080");
                        if (printWindow) {
                            printWindow.document.open();
                            printWindow.document.write(receiptHtml);
                            printWindow.document.close();
                        }
                    },
                    onError: error => {
                        console.error("인수증 출력 실패:", error);
                    },
                },
            );
        });
    };
    const handleCopy = async () => {
        const container = document.getElementById(`copy-target-${selectedIdx}`);
        const text = Array.from(container?.querySelectorAll("p") || [])
            .map(p => p.textContent?.trim() ?? "")
            .join("\n");

        try {
            await navigator.clipboard.writeText(text);
            addToast({
                type: "success",
                message: "복사 하였습니다.",
            });
        } catch {
            addToast({
                type: "error",
                message: "복사 실패하였습니다.",
            });
        }
    };

    /* 명의·경조사어처럼 값 하나만 복사하는 버튼용. handleCopy 는 템플릿 메시지 전체를 긁어간다 */
    const handleCopyText = async (text: string) => {
        const value = text?.trim() ?? "";

        if (!value) {
            addToast({
                type: "error",
                message: "복사할 내용이 없습니다.",
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(value);
            addToast({
                type: "success",
                message: "복사 하였습니다.",
            });
        } catch {
            addToast({
                type: "error",
                message: "복사 실패하였습니다.",
            });
        }
    };

    const handleProductSelect = (product: any) => {
        setSelectedProduct({
            product_idx: product.product_idx,
            product_image: product?.product_image?.file_name,
            product_category: product.product_category.category_name,
            product_category_idx: product.product_category.product_category_idx,
            product_union: product.product_category.union_option,
            product_code: product.product_code,
            product_code_name: product.product_code_name,
            product_name: product.product_name,
            product_amount: product.product_amount,
        });
        if (product.product_amount) {
            setSalesAmount(product.product_amount * productQuantity);
        }
    };
    const openImageUploadPop = (type: "ready" | "done" | "file" | "product") => {
        if (type === "ready") {
            setReadyUploadPop(true);
            setDoneUploadPop(false);
            setProductUploadPop(false);
            setUploadPop(false);
            setTempReadyImages([...readyImages]);
        } else if (type === "done") {
            setDoneUploadPop(true);
            setReadyUploadPop(false);
            setProductUploadPop(false);
            setUploadPop(false);
            setTempDoneImages([...doneImages]);
        } else if (type === "product") {
            setProductUploadPop(true);
            setReadyUploadPop(false);
            setDoneUploadPop(false);
            setUploadPop(false);
            setTempProductImages(productImages);

            setTempProductImageIndexes(productImages.map((_, idx) => idx));
        } else {
            setUploadPop(true);
            setDoneUploadPop(false);
            setProductUploadPop(false);
            setReadyUploadPop(false);
            setTempFileImages([...files]);
        }
    };
    const handleImageDrop = (e: React.DragEvent<HTMLElement>, type: "ready" | "done" | "file" | "product") => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        handleFiles(files, type);
    };

    const handleFiles = (files: File[], type: "ready" | "done" | "file" | "product") => {
        if (type === "ready") {
            setTempReadyImages(prev => [...prev, ...files]);
        } else if (type === "done") {
            setTempDoneImages(prev => [...prev, ...files]);
        } else if (type === "product") {
            setTempProductImages(prev => [...prev, ...files.map(file => ({ url: file }))].slice(0, 3));
        } else {
            setTempFileImages(prev => [...prev, ...files].slice(0, 4));
        }
    };
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, type: "ready" | "done" | "file" | "product") => {
        if (e.target.files) {
            const files = Array.from(e.target.files);

            const currentCount =
                type === "ready"
                    ? tempReadyImages.length
                    : type === "done"
                      ? tempDoneImages.length
                      : type === "product"
                        ? tempProductImages.length
                        : tempFileImages.length;

            const maxCount = type === "product" ? 3 : type === "file" ? 4 : Infinity;
            if (files.length + currentCount > maxCount) {
                showPop({
                    title: `이미지는 최대 ${maxCount}장까지 <br /> 등록할 수 있습니다.`,
                    type: "negative",
                    onRightClick: () => {},
                });
                return;
            }

            const newFiles = [...files].slice(0, maxCount - currentCount);

            if (type === "ready") {
                setTempReadyImages(prev => [...prev, ...newFiles]);
            } else if (type === "done") {
                setTempDoneImages(prev => [...prev, ...newFiles]);
            } else if (type === "product") {
                setTempProductImages(prev => [...prev, ...newFiles.map(file => ({ url: file }))]);
            } else {
                setTempFileImages(prev => [...prev, ...newFiles]);
            }
        }
    };

    const handleRemoveImage = (index: number, type: "ready" | "done" | "file" | "product", isPopup: boolean = false) => {
        const getImageList = (): (string | File | ImageItem)[] => {
            if (isPopup) {
                return type === "ready"
                    ? tempReadyImages
                    : type === "done"
                      ? tempDoneImages
                      : type === "product"
                        ? tempProductImages
                        : tempFileImages;
            } else {
                return type === "ready" ? readyImages : type === "done" ? doneImages : type === "product" ? productImages : files;
            }
        };

        const updateImageList = (updated: (string | File | ImageItem)[]) => {
            const items = updated.map(item => (typeof item === "object" && "url" in item ? item.url : item));

            if (isPopup) {
                if (type === "ready") setTempReadyImages(items);
                else if (type === "done") setTempDoneImages(items);
                else if (type === "product") setTempProductImages(updated as ImageItem[]);
                else setTempFileImages(items);
            } else {
                if (type === "ready") setReadyImages(items);
                else if (type === "done") setDoneImages(items);
                else if (type === "product") setProductImages(updated as ImageItem[]);
                else setFiles(items);
            }
        };

        const images = getImageList();
        const removed = images[index];
        const updatedList = [...images.slice(0, index), ...images.slice(index + 1)];
        if (typeof removed === "object" && removed !== null && "file_idx" in removed && typeof removed.file_idx === "number") {
            setDelFileIdx(prev => [...prev, removed.file_idx || 0]);
        } else if (typeof removed === "string") {
            const targetUrl = removed;
            const fileList =
                type === "ready"
                    ? (data?.product_info?.ready_image ?? [])
                    : type === "done"
                      ? (data?.product_info?.delivered_image ?? [])
                      : (data?.recipient_info?.order_file ?? []);

            const matchedList = fileList.filter(f => `${api_uri}/${f.file_name}` === targetUrl);

            const sameUrlBeforeCount = images.slice(0, index).filter(img => typeof img === "string" && img === targetUrl).length;

            const matched = matchedList[sameUrlBeforeCount];

            if (matched?.file_idx) {
                setDelFileIdx(prev => [...prev, matched.file_idx]);
            }
        }

        updateImageList(updatedList);
    };

    const handleConfirmImages = (type: "ready" | "done" | "file" | "product") => {
        if (type === "ready") {
            setReadyImages(tempReadyImages);
            setReadyUploadPop(false);
        } else if (type === "done") {
            setDoneImages(tempDoneImages);
            setDoneUploadPop(false);
        } else if (type === "product") {
            setProductImages(
                tempProductImages.map(img => ({
                    url: img.url,
                    file_idx: img.file_idx,
                })),
            );
            setProductUploadPop(false);
        } else {
            setFiles(tempFileImages);
            setUploadPop(false);
        }
    };
    const orderStatusMap: Record<number, string> = {
        1: "미확인",
        2: "접수완료",
        3: "준비완료",
        4: "배송완료",
        5: "주문취소",
        6: "보류",
        7: "삭제",
    };

    const { data: senderData } = useGetCommonSender(customerIdx?.toString(), senderTxt, union, selectedProduct.product_category_idx);
    const { data: wordData } = useGetCommonWords(wordTxt);
    const { data: memoData } = useGetCommonMemo();
    const { data: eventData } = useGetCommonEvent();
    const { data: event2Data } = useGetCommonEvent(Number(event));

    const putOrderStatusMutation = usePutOrderStatus();
    const deleteOrderMutation = useDeleteOrder();
    const putOrderInfoMutation = usePutOrderInfo(() => {
        addToast({
            type: "success",
            message: `${data?.member_info.customer_name} ${data?.member_info.order_name} <br />${data?.order_number} 주문건이 수정되었습니다.`,
            height: true,
        });
    }, selectedIdx);
    const handlePreviewImage = (image: string | File) => {
        const imageUrl = typeof image === "string" ? image : URL.createObjectURL(image);
        setPreviewImage(imageUrl);
    };

    const photoCheckMutation = usePostOrderPhotoCheck();
    const previewOpenRef = useRef(false);

    useEffect(() => {
        previewOpenRef.current = previewImage !== null;
    }, [previewImage]);

    // 준비완료 사진 확인 히스토리 기록
    // 닫혀 있던 팝업이 실제로 열릴 때만 기록한다 (더블클릭으로 중복 적재되는 것을 방지)
    // 저장 완료된 이미지(string)만 대상 - 첨부만 하고 미저장한 이미지는 제외
    const handleReadyImagePreview = (image: string | File) => {
        const wasOpen = previewOpenRef.current;
        previewOpenRef.current = true;

        handlePreviewImage(image);

        if (!wasOpen && typeof image === "string" && selectedIdx) {
            photoCheckMutation.mutate(selectedIdx.toString());
        }
    };


    const handleStatusSubmit = (value?: number) => {
        showPop({
            title:
                value === 7
                    ? " 해당 주문건을 삭제하시겠습니까?"
                    : value === 1
                      ? "해당 주문건을 복구하시겠습니까?"
                      : ` ${orderStatusMap[orderStatus]}로 주문상태를 변경하시겠습니까?`,
            content: value === 1 ? "복구된 주문건은 주문상태 [미확인],<br />발주상태는 [본점]으로 복구됩니다." : "",
            rightButton: "확인",
            type: value === 7 ? "negative" : "positive",
            onRightClick: () => {
                putOrderStatusMutation.mutate(
                    {
                        order_idx: [selectedIdx],
                        order_status: value || orderStatus,
                    },
                    {
                        onSuccess: () => {
                            addToast({
                                type: "success",
                                message:
                                    value === 1
                                        ? "주문건이 복구되었습니다."
                                        : value === 7
                                          ? "삭제 상태로 변경되었습니다."
                                          : `${name}님이 ${orderStatusMap[orderStatus]} 상태로 변경하었습니다.`,
                            });
                        },
                        onError: (error: any) => {
                            const errorMessage = error?.response?.data?.message || "알 수 없는 오류가 발생했습니다.";

                            const isStartsWithCurrent = errorMessage.startsWith("현재");

                            showPop({
                                title: errorMessage,
                                content: isStartsWithCurrent ? "동일한 접수상태이므로 <br />상태를 변경하실 수 없습니다." : undefined,
                                rightButton: undefined,
                                type: "negative",
                                onRightClick: () => {},
                            });
                        },
                    },
                );
            },
        });
    };

    const handleDeleteSubmit = () => {
        showPop({
            title: " 해당 주문건을 영구적으로 <br/> 삭제하시겠습니까?",
            rightButton: "확인",
            type: "negative",
            onRightClick: () => {
                deleteOrderMutation.mutate(
                    {
                        order_idx: [selectedIdx],
                    },
                    {
                        onSuccess: () => {
                            addToast({
                                type: "error",
                                message: "해당 주문건이 영구적으로 삭제되었습니다.",
                            });
                        },
                        onError: (error: any) => {
                            const errorMessage = error?.response?.data?.message || "알 수 없는 오류가 발생했습니다.";

                            const isStartsWithCurrent = errorMessage.startsWith("현재");

                            showPop({
                                title: errorMessage,
                                rightButton: undefined,
                                type: "negative",
                                onRightClick: () => {},
                            });
                        },
                    },
                );
            },
        });
    };
    const handleSubmit = () => {
        let hasError = false;

        if (!receiver.trim()) {
            setReceiverError(true);
            hasError = true;
        } else {
            setReceiverError(false);
        }

        if (!receiverMobile.trim()) {
            setReceiverMobileError(true);
            hasError = true;
        } else {
            setReceiverMobileError(false);
        }
        if (!immediate && (!deliveryHour || (!isTextOnlyHour && (!deliveryMinute || !deliveryCondition)))) {
            setTimeError(true);
            hasError = true;
        } else {
            setTimeError(false);
        }

        if (hasError) return;

        putOrderInfoMutation.mutate(
            {
                cancel_reason_idx: Number(cancel),
                cancel_reason: cancelDetail,
                order_name: orderName,
                order_mobile: orderMobile,
                product_idx: selectedProduct.product_idx || 0,
                sales_amount: salesAmount,
                order_amount: orderAmount,
                ready_image: readyImages.filter((image): image is File => image instanceof File),
                delivered_image: doneImages.filter((image): image is File => image instanceof File),
                del_file_idx: delFileIdx,
                // delivery_idx / delivery_amount 는 보내지 않는다.
                // 배송금액은 지점 응답에서 마스킹돼 0 으로 잡히는데, 서버가 ?? 로 받아
                // 0 을 그대로 저장해버린다(order.js:1882). 생략하면 기존 값이 유지된다.
                delivery_memo_idx: isNaN(Number(memo)) ? null : Number(memo),
                delivery_memo: memo === 0 ? "" : customMemo,
                wreath_sender: customSender,
                wreath_word: customWord,
                recipient_name: receiver,
                recipient_mobile: receiverMobile,
                is_union: union,
                event_content_idx: Number(event2),
                related_person: related,
                department: depart,
                company_tel: companyTel || null,
                work_location: companyAddress,
                delivery_immediate: immediate || undefined,
                delivery_date: selectedDate
                    ? new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000).toISOString().split("T")[0]
                    : undefined,
                delivery_time: immediate
                    ? ""
                    : isTextOnlyHour
                      ? hoursOptions.find(opt => opt.value === deliveryHour)?.label || ""
                      : `${deliveryHour}시 ${deliveryMinute}분 ${deliveryCondition}`,
                delivery_post: zoneCode,
                delivery_address: address,
                delivery_address_detail: detailAddress,
                order_file: files.filter((file): file is File => file instanceof File),
                file_memo: fileMemo,
                order_memo: orderMemo,
                event_content: eventContent,
                branch_type: selectedBranch === "본점" ? 1 : 2,
                customer_branch_idx: branch,
                branch_idx: Number(branchOption),
                branch_req: request,
                branch_product_idx: selectedProductIdx,
                branch_product_del_file_idx: delFileIdx,
                branch_product_image: productImages.filter(img => img.url instanceof File).map(img => img.url as File),
                customer_manager_idx: changedManagerIdxList.length > 0 ? changedManagerIdxList : null,
                customer_idx: Number(customerIdx),
                customer_name: customerName,
                customer_request: customerRequest,
                product_quantity: productQuantity,
            },
            {
                onError: (error: any) => {
                    const errorMessage = error?.response?.data?.message || "알 수 없는 오류가 발생했습니다.";
                    showPop({
                        title: errorMessage,
                        rightButton: undefined,
                        type: "positive",
                        onRightClick: () => {},
                    });
                },
            },
        );
    };
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setInputValue(page.toString());
    };

    const handlePageInput = (value: string) => {
        if (value.trim() === "") {
            setInputValue("");
            return;
        }
        if (!/^\d*$/.test(value)) {
            return;
        }
        const pageNumber = Number(value.trim());
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > (productData?.last_page || 1)) {
            alert("페이지 범위를 벗어났습니다.");
            setInputValue(value.slice(0, -1));
            return;
        }

        setInputValue(value);
        handlePageChange(pageNumber);
    };
    const handleAddressChange = (newZoneCode: string, newAddress: string, newDetailAddress: string) => {
        setZoneCode(newZoneCode);
        setAddress(newAddress);
        setDetailAddress(newDetailAddress);
    };

    const { data: productData } = useGetOrderProduct(currentPage ?? 1, search, searchTxt);

    const formatTelNumber = (value: string, prevValue: string, cursorPosition: number) => {
        const onlyNumbers = value.replace(/\D/g, ""); // 숫자만 유지
        let formatted = "";

        // 3자리 지역번호 목록
        const threeDigitAreaCodes = [
            "031",
            "032",
            "033",
            "041",
            "042",
            "043",
            "051",
            "052",
            "053",
            "054",
            "055",
            "061",
            "062",
            "063",
            "064",
        ];

        const isSeoul = onlyNumbers.startsWith("02");
        const isThreeDigitAreaCode = threeDigitAreaCodes.some(code => onlyNumbers.startsWith(code));
        const isSafeNumber = onlyNumbers.startsWith("050") && onlyNumbers.length === 12;
        const isMobileOrOther = !isSeoul && !isThreeDigitAreaCode && !isSafeNumber;

        // 번호 종류에 따라 최대 길이 설정
        const maxLength = isSafeNumber ? 12 : isSeoul ? 10 : 11;

        if (onlyNumbers.length > maxLength) return { formatted: prevValue, cursorOffset: 0 };

        if (isSafeNumber) {
            // 안심번호: 4-4-4 포맷
            formatted = `${onlyNumbers.slice(0, 4)}-${onlyNumbers.slice(4, 8)}-${onlyNumbers.slice(8, 12)}`;
        } else if (isSeoul) {
            if (onlyNumbers.length <= 2) {
                formatted = onlyNumbers;
            } else if (onlyNumbers.length <= 5) {
                formatted = `${onlyNumbers.slice(0, 2)}-${onlyNumbers.slice(2)}`;
            } else if (onlyNumbers.length <= 9) {
                formatted = `${onlyNumbers.slice(0, 2)}-${onlyNumbers.slice(2, 5)}-${onlyNumbers.slice(5)}`;
            } else {
                formatted = `${onlyNumbers.slice(0, 2)}-${onlyNumbers.slice(2, 6)}-${onlyNumbers.slice(6)}`;
            }
        } else if (isThreeDigitAreaCode || isMobileOrOther) {
            if (onlyNumbers.length <= 3) {
                formatted = onlyNumbers;
            } else if (onlyNumbers.length <= 6) {
                formatted = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3)}`;
            } else if (onlyNumbers.length <= 10) {
                formatted = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3, 6)}-${onlyNumbers.slice(6)}`;
            } else {
                formatted = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3, 7)}-${onlyNumbers.slice(7)}`;
            }
        }

        // 커서 위치 보정
        const dashCountBefore = (prevValue.slice(0, cursorPosition).match(/-/g) || []).length;
        const dashCountAfter = (formatted.slice(0, cursorPosition).match(/-/g) || []).length;
        const cursorOffset = dashCountAfter - dashCountBefore;

        return { formatted, cursorOffset };
    };

    const handlePhoneNumberChange = (type: "order_mobile" | "recipient_mobile" | "company_tel", value: string) => {
        const inputElement = document.activeElement as HTMLInputElement;
        const cursorPosition = inputElement.selectionStart ?? value.length;

        if (type === "order_mobile") {
            setOrderMobile(prev => {
                const { formatted, cursorOffset } = formatTelNumber(value, prev, cursorPosition);

                setTimeout(() => {
                    inputElement.setSelectionRange(cursorPosition + cursorOffset, cursorPosition + cursorOffset);
                }, 0);

                return formatted;
            });
        } else if (type === "recipient_mobile") {
            setReceiverMobile(prev => {
                const { formatted, cursorOffset } = formatTelNumber(value, prev, cursorPosition);

                setTimeout(() => {
                    inputElement.setSelectionRange(cursorPosition + cursorOffset, cursorPosition + cursorOffset);
                }, 0);

                return formatted;
            });
        } else if (type === "company_tel") {
            setCompanyTel(prev => {
                const { formatted, cursorOffset } = formatTelNumber(value, prev, cursorPosition);

                setTimeout(() => {
                    inputElement.setSelectionRange(cursorPosition + cursorOffset, cursorPosition + cursorOffset);
                }, 0);

                return formatted;
            });
        }
    };

    const handleCloseImageUploadPop = (type: "ready" | "done" | "file" | "product") => {
        if (type === "ready") {
            setReadyUploadPop(false);
        } else if (type === "done") {
            setDoneUploadPop(false);
        } else if (type === "product") {
            setProductUploadPop(false);
        } else {
            setUploadPop(false);
        }
    };

    const handleResetToInitialData = () => {
        if (!initialData) return;

        setBranchError(false);
        setOrderStatus(initialData.status_info.order_status || 0);
        setSelectedBranch(initialData.status_info.branch_name === "본점" ? initialData.status_info.branch_name : "타지점");
        setOrderName(initialData.member_info.order_name || "");
        setOrderMobile(initialData.member_info.order_mobile || "");
        setSalesAmount(initialData.product_info.sales_amount || 0);
        setCustomerRequest(initialData.product_info.customer_request || "");
        setProductQuantity(initialData.product_info.product_quantity || 0);
        setOrderAmount(initialData.product_info.order_amount || 0);
        setReadyImages(initialData.product_info.ready_image?.map((img: any) => `${api_uri}/${img.file_name}`) ?? []);
        setDoneImages(initialData.product_info.delivered_image?.map((img: any) => `${api_uri}/${img.file_name}`) ?? []);
        setReceiver(initialData.recipient_info.recipient_name || "");
        setReceiverMobile(initialData.recipient_info.recipient_mobile || "");
        setRelated(initialData.recipient_info.related_person || "");
        setDepart(initialData.recipient_info.department || "");
        setCompanyTel(initialData.recipient_info.company_tel || "");
        setCompanyAddress(initialData.recipient_info.work_location || "");
        setSelectedDate(initialData.recipient_info.delivery_date ? new Date(initialData.recipient_info.delivery_date) : new Date());
        setSelectedBranch(initialData.status_info.branch_name === "본점" ? initialData.status_info.branch_name : "타지점");
        setBranch(data?.status_info.customer_branch_idx || 0);
        setBranchLabel(data?.status_info.branch_name || "");
        setBranchOptionLabel(data?.status_info.branch_option || "");
        if (branchOptionData && data?.status_info?.branch_option) {
            const matchedBranch = branchOptionData.find(branchItem => branchItem.branch_option === data.status_info.branch_option);
            if (matchedBranch) {
                setBranchOption(matchedBranch.branch_idx);
            } else {
            }
        }
        setRequest(data?.status_info.branch_req || "");
        if (data?.status_info.branch_product_image?.length) {
            setProductImages(
                data.status_info.branch_product_image.map(img => ({
                    url: `${api_uri}/${img.file_name}`,
                    file_idx: img.file_idx,
                })),
            );
        } else {
            setProductImages([]);
            setSelectedProductIdx([]);
        }
        if (initialData.recipient_info.delivery_time) {
            const match = initialData.recipient_info.delivery_time.match(/(\d{1,2})시 (\d{2})분 (\S+)/);
            if (match) {
                setDeliveryHour(match[1].padStart(2, "0"));
                setDeliveryMinute(match[2]);
                setDeliveryCondition(match[3] || "");
            }
        } else {
            setDeliveryHour("");
            setDeliveryMinute("");
            setDeliveryCondition("까지");
        }

        setSelectedProduct({
            product_idx: initialData.product_info.product_idx || 0,
            product_code: initialData.product_code,
            product_code_name: initialData.product_code_name,
            product_image: initialData.product_info.product_image?.file_name || "",
            product_category: initialData.product_info.product_category || "",
            product_union: initialData.product_info.product_union || "",
            product_category_idx: initialData.product_info.product_category_idx || 0,
            product_name: initialData.product_info.product_name || "",
            product_amount: initialData.product_info.product_amount || 0,
        });

        setCustomerIdx(initialData.member_info.customer_idx || 0);
        setUnion(initialData.recipient_info.is_union || false);
        setImmediate(initialData.recipient_info.delivery_immediate || false);
        setZoneCode(initialData.recipient_info.delivery_post || "");
        setAddress(initialData.recipient_info.delivery_address || "");
        setDetailAddress(initialData.recipient_info.delivery_address_detail || "");
        setFileMemo(initialData.recipient_info.file_memo || "");

        setFiles(initialData.recipient_info.order_file ?? []);
    };

    const [hasInitializedSender, setHasInitializedSender] = useState(false);
    const clickedEventRef = useRef<string | undefined>(undefined);
    const handleEventChange = (value: string) => {
        setEvent(value);
        clickedEventRef.current = value;
    };

    useEffect(() => {
        setInitialData(data);
        setOrderStatus(data?.status_info.order_status || 0);
        setSelectedBranch(data?.status_info.branch_name === "본점" ? data?.status_info.branch_name : "타지점");
        setOrderName(data?.member_info.order_name || "");
        setOrderMobile(data?.member_info.order_mobile || "");
        setSalesAmount(data?.product_info.sales_amount || 0);
        setCustomerRequest(data?.product_info.customer_request || "");
        setProductQuantity(data?.product_info.product_quantity || 0);
        setOrderAmount(data?.product_info.order_amount || 0);
        setReadyImages(data?.product_info.ready_image?.map(img => `${api_uri}/${img.file_name}`) ?? []);
        setDoneImages(data?.product_info.delivered_image?.map(img => `${api_uri}/${img.file_name}`) ?? []);
        setReceiver(data?.recipient_info.recipient_name || "");
        setReceiverMobile(data?.recipient_info.recipient_mobile || "");
        setRelated(data?.recipient_info.related_person || "");
        setDepart(data?.recipient_info.department || "");
        setCompanyTel(data?.recipient_info.company_tel || "");
        setCompanyAddress(data?.recipient_info.work_location || "");
        if (data?.recipient_info.delivery_time) {
            const textOnlyLabels = ["오전중", "오후중", "당일배송"];
            const time = data.recipient_info.delivery_time.trim();

            if (textOnlyLabels.includes(time)) {
                setDeliveryHour(time); // "오전중"
                setDeliveryMinute("");
                setDeliveryCondition("");
            } else {
                const match = time.match(/(\d{1,2})시 (\d{2})분 (\S+)/);
                if (match) {
                    setDeliveryHour(match[1].padStart(2, "0")); // "08"
                    setDeliveryMinute(match[2]); // "00"
                    setDeliveryCondition(match[3]); // "까지"
                } else {
                    setDeliveryHour("");
                    setDeliveryMinute("");
                    setDeliveryCondition("");
                }
            }
        } else {
            setDeliveryHour("");
            setDeliveryMinute("");
            setDeliveryCondition("");
        }
        setSelectedProduct({
            product_idx: data?.product_info.product_idx || 0,
            product_code: data?.product_info.product_code || "",
            product_code_name: data?.product_info.product_code_name || "",
            product_image: data?.product_info.product_image?.file_name || "",
            product_category: data?.product_info.product_category || "",
            product_category_idx: data?.product_info.product_category_idx || 0,
            product_union: data?.product_info.product_category_union_option || false,
            product_name: data?.product_info.product_name || "",
            product_amount: data?.product_info.product_amount || 0,
        });
        setRequest(data?.status_info.branch_req || "");
        setCustomerIdx(data?.member_info.customer_idx || 0);
        setUnion(data?.recipient_info.is_union || false);
        setImmediate(data?.recipient_info.delivery_immediate || false);
        setZoneCode(data?.recipient_info.delivery_post || "");
        setAddress(data?.recipient_info.delivery_address || "");
        setDetailAddress(data?.recipient_info.delivery_address_detail || "");
        setFileMemo(data?.recipient_info.file_memo || "");

        const matchedUnionSender = senderData?.data?.find(sender => sender.union_sender === data?.recipient_info.wreath_sender);
        const matchedSender = senderData?.data?.find(sender => sender.default_sender === data?.recipient_info.wreath_sender);

        if (data?.product_info.product_category_union_option) {
            if (matchedUnionSender) {
                setSender(matchedUnionSender.sender_idx.toString());
                setCustomSender(matchedUnionSender.union_sender);
            } else {
                setSender("custom");
                setCustomSender(data?.recipient_info.wreath_sender || "");
            }
        } else {
            if (matchedSender) {
                setSender(matchedSender.sender_idx.toString());
                setCustomSender(matchedSender.default_sender);
            } else {
                setSender("custom");
                setCustomSender(data?.recipient_info.wreath_sender || "");
            }
        }

        const matchedWord = wordData?.data?.find(word => word.wreath_word === data?.recipient_info.wreath_word);
        if (matchedWord) {
            setWord(matchedWord.word_idx.toString());
            setCustomWord(matchedWord.wreath_word);
        } else {
            setWord("custom");
            setCustomWord(data?.recipient_info.wreath_word || "");
        }
        const matchedMemo = memoData?.data?.find(memo => memo.delivery_memo_idx === data?.recipient_info.delivery_memo_idx);
        if (matchedMemo) {
            setMemo(matchedMemo.delivery_memo_idx.toString());
            setCustomMemo(data?.recipient_info.delivery_memo || "");
        } else {
            setMemo(data?.recipient_info.delivery_memo_idx?.toString() || "1");
            setCustomMemo(data?.recipient_info.delivery_memo || "");
        }
        setOrderMemo(data?.recipient_info.order_memo || "");
        data?.recipient_info.event_content.depth1_idx === 2 && setEventContent(data?.recipient_info.event_content.event_content);
        setEvent(data?.recipient_info.event_content.depth1_idx || "");
        setEvent2(data?.recipient_info.event_content.event_content_idx || "");
        setBranch(data?.status_info.customer_branch_idx || 0);
        setBranchLabel(data?.status_info.branch_name || "");
        setBranchOptionLabel(data?.status_info.branch_option || "");

        setCancel(data?.status_info.cancel_reason_idx || 0);
        setCancelDetail(data?.status_info.cancel_reason || "");
        setFiles(data?.recipient_info.order_file?.map(img => `${api_uri}/${img.file_name}`) ?? []);
        if (data?.status_info.branch_product_image?.length) {
            setProductImages(
                data.status_info.branch_product_image.map(img => ({
                    url: `${api_uri}/${img.file_name}`,
                    file_idx: img.file_idx,
                })),
            );
        } else {
            setProductImages([]);
            setSelectedProductIdx([]);
        }
    }, [data]);
    useEffect(() => {
        if (branchOptionData && data?.status_info?.branch_option) {
            const matchedBranch = branchOptionData.find(branchItem => branchItem.branch_option === data.status_info.branch_option);
            if (matchedBranch) {
                setBranchOption(matchedBranch.branch_idx);
            } else {
            }
        }
    }, [branchOptionData]);
    useEffect(() => {
        if (!senderData || hasInitializedSender) return;

        const matchedUnionSender = senderData.data.find(
            sender => sender.union_sender?.trim() === data?.recipient_info.wreath_sender?.trim(),
        );
        const matchedSender = senderData.data.find(sender => sender.default_sender?.trim() === data?.recipient_info.wreath_sender?.trim());

        if (data?.product_info.product_category_union_option) {
            if (matchedUnionSender) {
                setSender(matchedUnionSender.sender_idx.toString());
                setCustomSender(matchedUnionSender.union_sender);
            } else {
                setSender("custom");
                setCustomSender(data?.recipient_info.wreath_sender || "");
            }
        } else {
            if (matchedSender) {
                setSender(matchedSender.sender_idx.toString());
                setCustomSender(matchedSender.default_sender);
            } else {
                setSender("custom");
                setCustomSender(data?.recipient_info.wreath_sender || "");
            }
        }

        setHasInitializedSender(true);
    }, [senderData]);
    useEffect(() => {
        if (data?.order_number) {
            onOrderNumberChange(data.order_number);
        }
    }, [data?.order_number, selectedIdx]);
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            const items = e.clipboardData?.items;
            if (items) {
                const images: File[] = [];
                for (let i = 0; i < items.length; i++) {
                    if (items[i].kind === "file") {
                        const file = items[i].getAsFile();
                        if (file && file.type.startsWith("image/")) {
                            images.push(file);
                        }
                    }
                }
                if (images.length > 0) {
                    if (readyUploadPop) {
                        handleFiles(images, "ready");
                    } else if (doneUploadPop) {
                        handleFiles(images, "done");
                    } else if (productUploadPop) {
                        handleFiles(images, "product");
                    } else if (uploadPop) {
                        handleFiles(images, "file");
                    }
                }
            }
        };
        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [readyUploadPop, doneUploadPop, uploadPop, productUploadPop]);
    useEffect(() => {
        if (onRefresh) {
            onRefresh(refetch);
        }
    }, [onRefresh]);
    useEffect(() => {
        if (event === "1") {
            setEvent2("1");
        }
    }, [event]);
    useEffect(() => {
        if (clickedEventRef.current !== undefined && clickedEventRef.current !== "1" && event2Data?.data?.length) {
            setEvent2(event2Data.data[0].event_content_idx.toString());
            clickedEventRef.current = undefined;
        }
    }, [event2Data]);
    useEffect(() => {
        if (branchData) {
            special && branchData[0]?.customer_name === "베스트플라워" && setBranch(branchData[0]?.customer_idx);
        }
    }, [special, branchData]);
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // 자동 조절
        }
    }, [fileMemo]);
    /* 조회로 값이 채워졌을 때도 줄 수에 맞춰 펴지도록 (입력 중에는 onInput 이 처리) */
    useEffect(() => {
        if (requestRef.current) {
            requestRef.current.style.height = "auto";
            requestRef.current.style.height = `${requestRef.current.scrollHeight}px`;
        }
    }, [request, selectedOption]);
    useEffect(() => {
        if (customerRequestRef.current) {
            customerRequestRef.current.style.height = "auto";
            customerRequestRef.current.style.height = `${customerRequestRef.current.scrollHeight}px`;
        }
    }, [customerRequest]);
    useEffect(() => {
        if (isBranchRequestPreset(request)) {
            setSelectedOption(request);
        } else {
            setSelectedOption(BRANCH_REQUEST_DIRECT_INPUT);
        }
    }, [request]);
    useEffect(() => {
        if (data?.customer_manager) {
            const initial = data.customer_manager.reduce(
                (acc, item) => {
                    const key = item.customer_manager_idx?.toString() ?? String(item.manager_mobile);
                    acc[key] = item.alrimtalk_flag;
                    return acc;
                },
                {} as { [key: string]: boolean },
            );

            setCheckedManager(initial);
            setInitialCheckedManager(initial);
        }
        if (data?.member_info.special) {
            if (data?.status_info.payment_status_text === "입금완료") {
                setCustomerIdx(data?.member_info.customer_idx);
                setCustomerName(data?.member_info.customer_name || "");
                setIsCustomInput(true);
            } else {
                // setCustomerIdx("custom");
                setCustomerIdx(data?.member_info.customer_idx);
                setCustomerName(data?.member_info.customer_name || "");
                setIsCustomInput(true);
            }
        } else {
            setCustomerIdx(data?.member_info.customer_idx || "");
            setCustomerName(data?.member_info.customer_name ?? "");
        }
    }, [data]);
    useEffect(() => {
        if (data?.recipient_info?.delivery_date) {
            const date = parseDate(data.recipient_info.delivery_date);
            if (date instanceof Date && !isNaN(date.getTime())) {
                setSelectedDate(date);
            } else {
                console.warn("Invalid date in Safari:", data.recipient_info.delivery_date);
            }
        }
    }, [data?.recipient_info?.delivery_date]);

    const { data: customerInfoData } = useGetCustomerInfo(data?.member_info?.customer_idx ?? 0);


    return (
        <div className="bg-gray6 pb-4 pl-4 flex flex-col">
            <button
                className="bg-white border-b text-sm py-2 shadow-custom rounded-tr-xl"
                onClick={() => {
                    printJS({
                        printable: "print-area",
                        type: "html",
                        scanStyles: true,
                        targetStyles: ["*"],
                        style: `
  @media print {
    .no-print {
      display: none !important;
    }
    
    
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      height: 100% !important;
      background: white !important;
    }

    #print-area {
      position: fixed !important;
      top: 200px !important;
      left: 0 !important;
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 20px !important;
      padding: 0px !important;
      zoom: 0.60 !important;
    }


    .print-box {
      width: 48% !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      background: white !important;
      box-sizing: border-box !important;
    }

    * {
      box-shadow: none !important;
      transform: none !important;
            position: static !important;
      max-width: 100% !important;
    }
  }
`,
                    });
                }}
            >
                주문판매 상세 출력
            </button>

            <div id="print-area" className="print-scale flex flex-col ">
                <div>
                    <div className="p-3 bg-white shadow-x-custom rounded-b-xl mb-2">
                        <div className="flex justify-between">
                            <div className="flex items-center gap-2">
                                <p className="font-bold text-xl">{data?.order_number}</p>
                                <p className="text-sm text-gray2">{data?.delivery_date}</p>
                            </div>
                            {level !== 7 && orderStatus !== 7 ? (
                                <div className="flex gap-1">
                                    <Button size="S" className="px-2" onClick={handlePrintReceipt}>
                                        인수증 출력
                                    </Button>
                                    <Button size="S" status="error" className="px-2" onClick={handleBranchDelete}>
                                        삭제
                                    </Button>
                                </div>
                            ) : (
                                level !== 7 && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleStatusSubmit(1)}
                                            className={`bg-primary3 text-sm text-primary1 px-2 py-1.5 rounded-md`}
                                        >
                                            주문판매 내역으로 복구
                                        </button>
                                        <Button status="error" size="S" className="px-2" onClick={() => handleDeleteSubmit()}>
                                            영구 삭제
                                        </Button>
                                    </div>
                                )
                            )}
                        </div>
                        <p className="text-sm text-gray2 mt-1">{data?.order_date.replaceAll("-", ".")} 주문</p>
                        <div className="flex gap-3 flex-col">
                            <div className="flex justify-between items-center">
                                <p className="text-gray2 font-bold">주문 상태</p>
                                <DownArrow
                                    onClick={() => setIsOrderOpen(!isOrderOpen)}
                                    className={`${isOrderOpen ? "rotate-180" : "rotate-0"} transition-transform cursor-pointer`}
                                />
                            </div>
                            {isOrderOpen && (
                                <div className="border-t-2 border-gray2 ">
                                    <div className="flex border-b border-gray4">
                                        <TableName className="h-12" required>
                                            주문상태
                                        </TableName>
                                        <div className="flex items-center pl-2 gap-2">
                                            {orderStatus === 5 || orderStatus === 7 ? (
                                                <div className="w-[120px] px-3 py-1.5 border border-gray4 rounded-lg text-sm">
                                                    {orderStatus === 5 ? "취소" : "삭제"}
                                                </div>
                                            ) : (
                                                <>
                                                    <SelectBox
                                                        disabled={level === 7}
                                                        className="w-[100px]"
                                                        options={[
                                                            { value: "1", label: "미확인" },
                                                            { value: "2", label: "접수완료" },
                                                            { value: "3", label: "준비완료" },
                                                            { value: "4", label: "배송완료" },
                                                            { value: "6", label: "보류" },
                                                        ]}
                                                        onChange={value => setOrderStatus(Number(value))}
                                                        value={orderStatus.toString()}
                                                    />
                                                    {level !== 7 && (
                                                        <Button
                                                            size="S"
                                                            status="regist"
                                                            className="px-[18px]"
                                                            onClick={() => handleStatusSubmit()}
                                                        >
                                                            저장
                                                        </Button>
                                                    )}
                                                </>
                                            )}

                                            {data?.status_info.customer_manager?.customer_manager_date && (
                                                <div className="flex text-[13.5px] items-center gap-1">
                                                    <p className="whitespace-nowrap min-w-20">
                                                        {data?.status_info.customer_manager.customer_manager_date.slice(5)}
                                                    </p>
                                                    <p className="whitespace-nowrap">
                                                        {data?.status_info.customer_manager.customer_manager_name}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {orderStatus === 5 && (
                                        <>
                                            <div className="flex  border-b border-gray4">
                                                <TableName className="h-12" required>
                                                    취소사유
                                                </TableName>
                                                <SelectBox
                                                    disabled={level === 7}
                                                    options={
                                                        cancelData?.data
                                                            ? [
                                                                  ...cancelData.data.map(cancel => ({
                                                                      value: cancel.cancel_reason_idx.toString(),
                                                                      label: cancel.cancel_reason,
                                                                  })),
                                                              ]
                                                            : []
                                                    }
                                                    full
                                                    className="w-full m-[7px]"
                                                    value={cancel.toString()}
                                                    onChange={value => {
                                                        setCancel(value);
                                                    }}
                                                />
                                            </div>
                                            <div className="flex border-b border-gray4">
                                                <TableName className="h-12">상세사유</TableName>
                                                <Input
                                                    status={level === 7 ? "view" : undefined}
                                                    maxLength={100}
                                                    placeholder="100자"
                                                    className="p-2 w-full"
                                                    value={cancelDetail ?? ""}
                                                    onChange={e => setCancelDetail(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 mt-5 flex-col">
                            <div className="flex justify-between items-center">
                                <p className="text-gray2 font-bold">발주 상태</p>
                                <DownArrow
                                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                                    className={`${isStatusOpen ? "rotate-180" : "rotate-0"} transition-transform cursor-pointer`}
                                />
                            </div>
                            {isStatusOpen && (
                                <div className="border-t-2 border-gray2 ">
                                    <div className="flex border-b border-gray4 relative">
                                        <TableName className="flex">발주이미지</TableName>
                                        <div className="flex items-center justify-between w-full">
                                            <div className="flex gap-2 flex-wrap p-2 w-full">
                                                {productImages.length >= 1 && (
                                                    <div
                                                        className={`${productUploadPop ? "border-primary2 text-primary2" : " border-gray5 border-dashed text-grayicon"} ${
                                                            level === 7 ? "cursor-default" : "cursor-pointer"
                                                        } w-[96px] h-[96px] flex flex-col items-center justify-center border rounded-xl p-2`}
                                                        onClick={() => level !== 7 && openImageUploadPop("product")}
                                                    >
                                                        <PlusIcon />
                                                        <p className="text-sm">추가</p>
                                                    </div>
                                                )}
                                                {productImages.map((image, index) => (
                                                    <div
                                                        key={index}
                                                        className="relative w-[96px] h-[96px] border rounded-xl overflow-hidden"
                                                    >
                                                        <img
                                                            alt="image"
                                                            src={typeof image.url === "string" ? image.url : URL.createObjectURL(image.url)}
                                                            className="w-full h-full object-contain cursor-pointer"
                                                            onClick={() => handlePreviewImage(image.url)}
                                                        />

                                                        {level !== 7 && (
                                                            <button
                                                                className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                                onClick={() => {
                                                                    handleRemoveImage(index, "product", false);
                                                                    if (typeof image === "string" && selectedProductIdx.length > 0) {
                                                                        setSelectedProductIdx([]);
                                                                    }
                                                                }}
                                                            >
                                                                <CloseIcon />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}

                                                {productImages.length < 1 && (
                                                    <div
                                                        className={`
                                            ${productUploadPop ? "bg-primary4 border-primary2 text-primary2" : "border-gray5 border-dashed text-grayicon"}  ${level === 7 ? "cursor-default" : "cursor-pointer"}  w-full min-h-[96px] flex flex-col items-center justify-center border  rounded-xl p-2
                                        `}
                                                        onClick={() => level !== 7 && openImageUploadPop("product")}
                                                    >
                                                        {productUploadPop ? <UploadOnImage /> : <UploadImage />}
                                                        <p className="text-sm whitespace-nowrap">이미지 업로드</p>
                                                    </div>
                                                )}
                                            </div>
                                            <Button
                                                onClick={() => setBranchProductPop(true)}
                                                disabled={level === 7}
                                                size="S"
                                                className="px-2 whitespace-nowrap h-8"
                                                icon={<Select />}
                                                iconPosition="right"
                                            >
                                                검색등록
                                            </Button>
                                        </div>
                                        {/* 이미지 업로드 팝업 */}
                                        {productUploadPop && (
                                            <div className="flex flex-col z-[200] absolute w-[400px] bg-white rounded-2xl shadow-custom translate-x-[108px] translate-y-[73px]">
                                                <div className="flex w-full  items-center px-5 justify-between py-5 ">
                                                    <p className="text-primary1 text-2xl font-bold">이미지 업로드</p>
                                                    <p className="text-sm">최대 3장까지 업로드 가능합니다.</p>
                                                    <CloseBlackIcon
                                                        className="cursor-pointer"
                                                        onClick={() => handleCloseImageUploadPop("product")}
                                                    />
                                                </div>
                                                <div className="px-6 pb-5 flex flex-col gap-2">
                                                    <label
                                                        onDrop={e => handleImageDrop(e, "product")}
                                                        onDragOver={e => e.preventDefault()}
                                                        className="flex h-[160px] justify-center cursor-pointer flex-col items-center border border-gray5 border-dashed rounded-xl"
                                                    >
                                                        <UploadImage className="w-[72px] h-[72px] mb-2" />
                                                        <p className="text-gray2 text-sm">이미지 업로드</p>
                                                        <p className="text-gray3 text-sm">
                                                            (이미지를 드래그하거나 복사, 붙여넣기하여 등록)
                                                        </p>
                                                        <input
                                                            type="file"
                                                            className="hidden"
                                                            multiple
                                                            accept="image/*"
                                                            onChange={event => handleImageUpload(event, "product")}
                                                        />
                                                    </label>
                                                    <div className="flex gap-1">
                                                        {tempProductImages.map((image, index) => (
                                                            <div
                                                                key={index}
                                                                className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden"
                                                            >
                                                                <img
                                                                    src={
                                                                        typeof image.url === "string"
                                                                            ? image.url
                                                                            : URL.createObjectURL(image.url)
                                                                    }
                                                                    className="w-full h-full object-contain"
                                                                    alt="image"
                                                                />
                                                                <button
                                                                    className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                                    onClick={() =>
                                                                        handleRemoveImage(tempProductImageIndexes[index], "product", true)
                                                                    }
                                                                >
                                                                    <CloseIcon />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <Button
                                                        size="L"
                                                        status="regist"
                                                        className="w-full"
                                                        onClick={() => handleConfirmImages("product")}
                                                    >
                                                        이미지 등록
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        {/*  검색등록 팝업  */}
                                        {branchProductPop && (
                                            <Product
                                                onClose={() => {
                                                    setBranchProductPop(false);
                                                }}
                                                onSelect={({ imageUrl, idx }) => {
                                                    if (productImages.length >= 3) {
                                                        showPop({
                                                            title: "이미지는 최대 3장까지 <br /> 등록할 수 있습니다.",
                                                            type: "negative",
                                                            onRightClick: () => {},
                                                        });
                                                        return;
                                                    }

                                                    setProductImages(prev => [...prev, { url: imageUrl }]);
                                                    setSelectedProductIdx(prev => [...prev, idx]);
                                                    setBranchProductPop(false);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="flex border-b border-gray4">
                                        <TableName className="flex">
                                            타지점
                                            <br /> 요구사항
                                        </TableName>
                                        <div className="w-full flex flex-col px-2 py-2 gap-1">
                                            <SelectBox
                                                full
                                                onChange={(val: string) => {
                                                    setSelectedOption(val);
                                                    if (val !== BRANCH_REQUEST_DIRECT_INPUT) {
                                                        setRequest(val);
                                                    } else {
                                                        setRequest("");
                                                    }
                                                }}
                                                value={selectedOption}
                                                options={BRANCH_REQUEST_OPTIONS}
                                            />
                                            {selectedOption === BRANCH_REQUEST_DIRECT_INPUT && (
                                                <textarea
                                                    ref={requestRef}
                                                    disabled={level === 7}
                                                    className="px-3 h-auto py-[7px] placeholder:text-grayicon focus:border-primary1 outline-0 rounded-lg w-full text-sm border border-gray4 resize-none overflow-hidden"
                                                    placeholder="요구사항을 입력하세요"
                                                    value={request}
                                                    onChange={e => setRequest(e.target.value)}
                                                    onInput={e => {
                                                        e.currentTarget.style.height = "auto";
                                                        e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                                    }}
                                                    rows={1}
                                                    style={{ minHeight: "8px" }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex border-b border-gray4">
                                        <TableName className="flex">
                                            접수완료
                                            <br />
                                            템플릿
                                            <br />
                                            메시지
                                        </TableName>
                                        <div className="w-full py-2">
                                            <Button
                                                icon={<CopyIcon />}
                                                onClick={handleCopy}
                                                iconPosition="right"
                                                size="S"
                                                className="px-2 ml-auto"
                                            >
                                                복사
                                            </Button>

                                            <div id={`copy-target-${selectedIdx}`} className="w-full flex flex-col px-2 py-1 gap-1">
                                                <p>
                                                    배송일자: <span>{data?.recipient_info?.delivery_date}</span>
                                                </p>
                                                <p>
                                                    배송시간:{" "}
                                                    <span>
                                                        {data?.recipient_info?.delivery_immediate
                                                            ? "즉시배송"
                                                            : data?.recipient_info?.delivery_time}
                                                    </span>
                                                </p>
                                                <p>
                                                    명의: <span>{data?.recipient_info?.wreath_sender}</span>
                                                </p>
                                                <p>
                                                    경조사어: <span>{data?.recipient_info?.wreath_word}</span>
                                                </p>
                                                <p>
                                                    상품: <span>{data?.product_info?.product_code_name}</span>
                                                </p>
                                                <p>
                                                    발주금액: <span>{makeIntComma(data?.product_info?.order_amount || 0)}원</span>
                                                </p>
                                                <p>
                                                    받으시는분:{" "}
                                                    <span>
                                                        {data?.recipient_info?.recipient_name}{" "}
                                                        {data?.recipient_info?.event_content.depth1 === "선택안함"
                                                            ? "선택안함"
                                                            : data?.recipient_info?.event_content.depth2}{" "}
                                                    </span>
                                                </p>
                                                <p>
                                                    대상자명:{" "}
                                                    <span>
                                                        {data?.recipient_info?.related_person?.trim() === ""
                                                            ? "-"
                                                            : data?.recipient_info?.related_person}
                                                    </span>
                                                </p>
                                                <p>
                                                    배송지주소:{" "}
                                                    <span>
                                                        {data?.recipient_info?.delivery_post} {data?.recipient_info?.delivery_address}{" "}
                                                        {data?.recipient_info?.delivery_address_detail}
                                                    </span>
                                                </p>
                                                <p>
                                                    받으시는분 연락처: <span>{data?.recipient_info?.recipient_mobile}</span>
                                                </p>
                                                {/* <p>
                                                    인수증배송메모: <span>{data?.recipient_info?.delivery_memo}</span>
                                                </p> */}
                                                <p>
                                                    타지점요구사항: <span>{data?.status_info.branch_req}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {level !== 7 && orderStatus !== 7 && orderStatus !== 5 && (
                                        <div className="flex w-full gap-2 mt-3 no-print">
                                            <Button
                                                size="L"
                                                className="w-full"
                                                onClick={() => {
                                                    setSelectedBranch(
                                                        initialData.status_info.branch_name === "본점"
                                                            ? initialData.status_info.branch_name
                                                            : "타지점",
                                                    );
                                                    setBranch(data?.status_info.customer_branch_idx || 0);
                                                    setBranchLabel(data?.status_info.branch_name || "");
                                                    setBranchOptionLabel(data?.status_info.branch_option || "");
                                                    if (branchOptionData && data?.status_info?.branch_option) {
                                                        const matchedBranch = branchOptionData.find(
                                                            branchItem => branchItem.branch_option === data.status_info.branch_option,
                                                        );
                                                        if (matchedBranch) {
                                                            setBranchOption(matchedBranch.branch_idx);
                                                        } else {
                                                        }
                                                    }
                                                    setRequest(data?.status_info.branch_req || "");
                                                    if (data?.status_info.branch_product_image?.length) {
                                                        setProductImages(
                                                            data.status_info.branch_product_image.map(img => ({
                                                                url: `${api_uri}/${img.file_name}`,
                                                                file_idx: img.file_idx,
                                                            })),
                                                        );
                                                    } else {
                                                        setProductImages([]);
                                                        setSelectedProductIdx([]);
                                                    }
                                                }}
                                            >
                                                취소
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="px-3 pt-3 bg-white rounded-t-xl flex flex-col gap-5 shadow-custom">

                     


                        <div className="flex flex-col gap-3">
                            <div className="flex justify-between items-center">
                                <p className="text-gray2 font-bold">상품정보</p>
                                <DownArrow
                                    onClick={() => setIsProductOpen(!isProductOpen)}
                                    className={`${isProductOpen ? "rotate-180" : "rotate-0"} transition-transform cursor-pointer`}
                                />
                            </div>
                            {isProductOpen && (
                                <div className="border-t-2 border-gray2">
                                    <div className="flex border-b border-gray4 items-stretch">
                                        <TableName required>상품</TableName>
                                        <div className="flex p-2 items-center justify-between w-full gap-2">
                                            <div className="flex items-center gap-4">
                                                {selectedProduct.product_image && (
                                                    <div
                                                        className="cursor-pointer"
                                                        onClick={() => setImagePopup(`${api_uri}/${selectedProduct.product_image}`)}
                                                    >
                                                        <img
                                                            crossOrigin="anonymous"
                                                            className="w-[60px] h-[60px] object-contain rounded-xl flex items-center justify-center text-xs"
                                                            src={`${api_uri}/${selectedProduct.product_image}`}
                                                            alt="Product Image"
                                                            onError={e => {
                                                                e.currentTarget.onerror = null;
                                                                e.currentTarget.src = logo;
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                {imagePopup && (
                                                    <div
                                                        className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[101]"
                                                        onClick={() => setImagePopup(null)}
                                                    >
                                                        <img
                                                            src={imagePopup}
                                                            alt="Product Image"
                                                            className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex flex-col">
                                                    <div className="flex text-sm gap-2">
                                                        <p className="text-gray3 whitespace-nowrap">
                                                            {selectedProduct.product_category || data?.product_info?.product_category}
                                                        </p>
                                                        {selectedProduct.product_code ? (
                                                            <p className="whitespace-nowrap">
                                                                {/* {selectedProduct.product_code_name} / {selectedProduct.product_code} */}
                                                            </p>
                                                        ) : (
                                                            data?.product_info?.product_code && (
                                                                <p className="whitespace-nowrap">
                                                                    {/* {data?.product_info?.product_code_name} /{" "}
                                                                    {data?.product_info?.product_code} */}
                                                                </p>
                                                            )
                                                        )}
                                                    </div>

                                                    <div className="flex gap-2 items-center md:max-w-64 max-w-52">
                                                        <p className="text-lg font-bold">
                                                            {selectedProduct.product_name || data?.product_info?.product_name}
                                                        </p>
                                                        <p className="whitespace-nowrap">
                                                            {selectedProduct.product_amount.toLocaleString() ||
                                                                data?.product_info?.product_amount.toLocaleString()}
                                                            원
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex border-b border-gray4">
                                        <div className="flex items-stretch w-full">
                                            <TableName className="">고객 요청사항</TableName>
                                            <textarea
                                                ref={customerRequestRef}
                                                disabled={level === 7}
                                                maxLength={50}
                                                className="px-3 h-auto my-[7px] mx-2 py-[7px] placeholder:text-grayicon focus:border-primary1 outline-0 rounded-lg w-full text-sm border border-gray4 resize-none overflow-hidden"
                                                value={customerRequest}
                                                onChange={e => setCustomerRequest(e.target.value)}
                                                onInput={e => {
                                                    e.currentTarget.style.height = "auto";
                                                    e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                                }}
                                                rows={1}
                                                style={{ minHeight: "8px" }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex border-b border-gray4">
                                        <div className="flex items-center w-full">
                                            <TableName className="h-12">수량</TableName>
                                            <Input
                                                maxLength={4}
                                                quantity
                                                className="px-2 w-1/5"
                                                value={productQuantity.toLocaleString()}
                                                onChange={e => {
                                                    const value = e.target.value.replace(/[^0-9]/g, "");
                                                    const quantity = Number(value);
                                                    setProductQuantity(quantity);
                                                    if (selectedProduct.product_amount) {
                                                        setSalesAmount(selectedProduct.product_amount * quantity);
                                                    } else if (data?.product_info?.product_amount) {
                                                        setSalesAmount(data.product_info.product_amount * quantity);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {/* 판매금액은 지점에 노출하지 않는다 (응답에서도 마스킹된다) */}
                                    <div className="flex border-b border-gray4">
                                        <div className="flex items-center w-full">
                                            <TableName className="h-12">발주금액</TableName>
                                            {/* 본점이 정하는 금액이라 지점은 수정할 수 없다 (서버에서도 기존 값 고정) */}
                                            <Input
                                                status="view"
                                                price
                                                className="px-2"
                                                value={orderAmount === 0 ? "" : (orderAmount.toLocaleString() ?? "")}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex border-b border-gray4 items-stretch relative no-print">
                                        <TableName>상품준비완료</TableName>
                                        <div className="flex gap-2 flex-wrap p-2 w-full">
                                            {readyImages.length >= 1 && (
                                                <div
                                                    className={`${readyUploadPop ? "border-primary2 text-primary2" : " border-gray5 border-dashed text-grayicon"} ${
                                                        level === 7 ? "cursor-default" : "cursor-pointer"
                                                    } w-[60px] flex flex-col items-center border rounded-xl p-2`}
                                                    onClick={() => level !== 7 && openImageUploadPop("ready")}
                                                >
                                                    <PlusIcon />
                                                    <p className="text-sm">추가</p>
                                                </div>
                                            )}
                                            {readyImages.map((image, index) => (
                                                <div key={index} className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden">
                                                    <img
                                                        alt="image"
                                                        src={typeof image === "string" ? image : URL.createObjectURL(image)}
                                                        className="w-full h-full object-contain cursor-pointer"
                                                        onClick={() => handleReadyImagePreview(image)}
                                                    />
                                                    {level !== 7 && (
                                                        <button
                                                            className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                            onClick={() => handleRemoveImage(index, "ready", false)}
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}

                                            {readyImages.length < 1 && (
                                                <div
                                                    className={`
                                            ${readyUploadPop ? "bg-primary4 border-primary2 text-primary2" : "border-gray5 border-dashed text-grayicon"}  ${level === 7 ? "cursor-default" : "cursor-pointer"}  w-full flex flex-col items-center border  rounded-xl p-2
                                        `}
                                                    onClick={() => level !== 7 && openImageUploadPop("ready")}
                                                >
                                                    {readyUploadPop ? <UploadOnImage /> : <UploadImage />}
                                                    <p className="text-sm whitespace-nowrap">이미지 업로드</p>
                                                </div>
                                            )}
                                        </div>
                                        {/* 이미지 업로드 팝업 */}
                                        {readyUploadPop && (
                                            <div className="flex flex-col z-50 absolute w-[400px] bg-white rounded-2xl shadow-custom translate-x-[108px] translate-y-[73px]">
                                                <div className="flex w-full  items-center px-5 justify-between py-5 ">
                                                    <p className="text-primary1 text-2xl font-bold">이미지 업로드</p>
                                                    <p className="text-sm">최대 4장까지 업로드 가능합니다.</p>
                                                    <CloseBlackIcon
                                                        className="cursor-pointer"
                                                        onClick={() => handleCloseImageUploadPop("ready")}
                                                    />
                                                </div>
                                                <div className="px-6 pb-5 flex flex-col gap-2">
                                                    <label
                                                        onDrop={e => handleImageDrop(e, "ready")}
                                                        onDragOver={e => e.preventDefault()}
                                                        className="flex h-[160px] justify-center cursor-pointer flex-col items-center border border-gray5 border-dashed rounded-xl"
                                                    >
                                                        <UploadImage className="w-[72px] h-[72px] mb-2" />
                                                        <p className="text-gray2 text-sm">이미지 업로드</p>
                                                        <p className="text-gray3 text-sm">
                                                            (이미지를 드래그하거나 복사, 붙여넣기하여 등록)
                                                        </p>
                                                        <input
                                                            type="file"
                                                            className="hidden"
                                                            multiple
                                                            accept="image/*"
                                                            onChange={event => handleImageUpload(event, "ready")}
                                                        />
                                                    </label>
                                                    <div className="flex flex-wrap gap-1 max-w-[320px]">
                                                        {tempReadyImages.map((image, index) => (
                                                            <div
                                                                key={index}
                                                                className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden"
                                                            >
                                                                <img
                                                                    src={typeof image === "string" ? image : URL.createObjectURL(image)}
                                                                    className="w-full h-full object-contain"
                                                                    alt="image"
                                                                />
                                                                <button
                                                                    className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                                    onClick={() => handleRemoveImage(index, "ready", true)}
                                                                >
                                                                    <CloseIcon />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <Button
                                                        size="L"
                                                        status="regist"
                                                        className="w-full"
                                                        onClick={() => handleConfirmImages("ready")}
                                                    >
                                                        이미지 등록
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex border-b border-gray4 items-stretch relative no-print">
                                        <TableName>상품배송완료</TableName>
                                        <div className="flex gap-2 flex-wrap p-2 w-full">
                                            {doneImages.length >= 1 && (
                                                <div
                                                    className={`${doneUploadPop ? "border-primary2 text-primary2" : " border-gray5 border-dashed text-grayicon"}  ${level === 7 ? "cursor-default" : "cursor-pointer"}  w-[60px] flex flex-col items-center border rounded-xl p-2`}
                                                    onClick={() => level !== 7 && openImageUploadPop("done")}
                                                >
                                                    <PlusIcon />
                                                    <p className="text-sm">추가</p>
                                                </div>
                                            )}
                                            {doneImages.map((image, index) => (
                                                <div key={index} className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden">
                                                    <img
                                                        alt="image"
                                                        src={typeof image === "string" ? image : URL.createObjectURL(image)}
                                                        className="w-full h-full object-contain cursor-pointer"
                                                        onClick={() => handlePreviewImage(image)}
                                                    />
                                                    {level !== 7 && (
                                                        <button
                                                            className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                            onClick={() => handleRemoveImage(index, "done", false)}
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}

                                            {doneImages.length < 1 && (
                                                <div
                                                    className={`
                                            ${doneUploadPop ? "bg-primary4 border-primary2 text-primary2" : "border-gray5 border-dashed text-grayicon"}  ${level === 7 ? "cursor-default" : "cursor-pointer"}  w-full flex flex-col items-center border  rounded-xl p-2
                                        `}
                                                    onClick={() => level !== 7 && openImageUploadPop("done")}
                                                >
                                                    {doneUploadPop ? <UploadOnImage /> : <UploadImage />}
                                                    <p className="text-sm whitespace-nowrap">이미지 업로드</p>
                                                </div>
                                            )}
                                        </div>
                                        {/* 이미지 업로드 팝업 */}
                                        {doneUploadPop && (
                                            <div className="flex flex-col z-50 absolute w-[400px] bg-white rounded-2xl shadow-custom translate-x-[108px] translate-y-[73px]">
                                                <div className="flex w-full  items-center px-5 justify-between py-5 ">
                                                    <p className="text-primary1 text-2xl font-bold">이미지 업로드</p>
                                                    <p className="text-sm">최대 4장까지 업로드 가능합니다.</p>
                                                    <CloseBlackIcon
                                                        className="cursor-pointer"
                                                        onClick={() => handleCloseImageUploadPop("done")}
                                                    />
                                                </div>
                                                <div className="px-6 pb-5 flex flex-col gap-2">
                                                    <label
                                                        onDrop={e => handleImageDrop(e, "done")}
                                                        onDragOver={e => e.preventDefault()}
                                                        className="flex h-[160px] justify-center cursor-pointer flex-col items-center border border-gray5 border-dashed rounded-xl"
                                                    >
                                                        <UploadImage className="w-[72px] h-[72px] mb-2" />
                                                        <p className="text-gray2 text-sm">이미지 업로드</p>
                                                        <p className="text-gray3 text-sm">
                                                            (이미지를 드래그하거나 복사, 붙여넣기하여 등록)
                                                        </p>
                                                        <input
                                                            accept="image/*"
                                                            type="file"
                                                            className="hidden"
                                                            multiple
                                                            onChange={event => handleImageUpload(event, "done")}
                                                        />
                                                    </label>
                                                    <div className="flex gap-1">
                                                        {tempDoneImages.map((image, index) => (
                                                            <div
                                                                key={index}
                                                                className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden"
                                                            >
                                                                <img
                                                                    alt="image"
                                                                    src={typeof image === "string" ? image : URL.createObjectURL(image)}
                                                                    className="w-full h-full object-contain"
                                                                />
                                                                <button
                                                                    className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                                    onClick={() => handleRemoveImage(index, "done", true)}
                                                                >
                                                                    <CloseIcon />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <Button
                                                        size="L"
                                                        status="regist"
                                                        className="w-full"
                                                        onClick={() => handleConfirmImages("done")}
                                                    >
                                                        이미지 등록
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="px-3 pt-3 bg-white flex flex-col gap-5 shadow-custom">
                    <div className="flex flex-col gap-3">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2.5">
                                <p className="text-gray2 font-bold">수신정보</p>
                            </div>
                            <DownArrow
                                onClick={() => setIsReceiverOpen(!isReceiverOpen)}
                                className={`${isReceiverOpen ? "rotate-180" : "rotate-0"} transition-transform cursor-pointer`}
                            />
                        </div>
                        {isReceiverOpen && (
                            <div className="border-t-2 border-gray2 ">
                                <div className={`flex border-b border-gray4 items-stretch w-full`}>
                                    <TableName className="">명의</TableName>
                                    {/* 셀렉트 드롭다운이 absolute 라 overflow-hidden 을 주면 잘린다.
                                        폭 제어는 min-w-0 로만 하고, 긴 문구는 SelectBox 쪽에서 줄바꿈시킨다 */}
                                    <div className="flex flex-col w-full min-w-0 m-[7px] gap-[7px]">
                                        <SelectBox
                                            placeholder="선택"
                                            disabled={level === 7}
                                            onSearchChange={value => setSenderTxt(value)}
                                            searchable
                                            options={
                                                senderData?.data
                                                    ? [
                                                          { value: "custom", label: "직접입력" },
                                                          ...senderData.data.map(sender => ({
                                                              value: sender.sender_idx.toString(),
                                                              label: selectedProduct.product_union
                                                                  ? sender.union_sender.replace(/</g, "&lt;").replace(/>/g, "&gt;")
                                                                  : sender.default_sender.replace(/</g, "&lt;").replace(/>/g, "&gt;"),
                                                          })),
                                                      ]
                                                    : [{ value: "custom", label: "직접입력" }]
                                            }
                                            full
                                            className="w-full"
                                            value={sender.toString()}
                                            onChange={value => {
                                                if (value === "custom") {
                                                    setSender(value);
                                                    setCustomSender("");
                                                } else {
                                                    const selectedSender = senderData?.data.find(
                                                        sender => sender.sender_idx.toString() === value,
                                                    );
                                                    if (selectedSender) {
                                                        setSender(value);
                                                        setCustomSender(
                                                            selectedProduct.product_union
                                                                ? selectedSender.union_sender
                                                                : selectedSender.default_sender,
                                                        );
                                                    }
                                                }
                                            }}
                                        />

                                        {sender === "custom" && (
                                            <textarea
                                                disabled={level === 7}
                                                className="px-3 py-2 h-auto placeholder:text-grayicon focus:border-primary1 outline-0 rounded-lg w-full text-sm border border-gray4 resize-none overflow-hidden"
                                                rows={1}
                                                style={{ minHeight: "8px" }}
                                                value={customSender ?? ""}
                                                onChange={e => setCustomSender(e.target.value)}
                                                onInput={e => {
                                                    e.currentTarget.style.height = "auto";
                                                    e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center shrink-0 px-[7px]">
                                        <Button
                                            icon={<CopyIcon />}
                                            iconPosition="right"
                                            size="S"
                                            className="px-2"
                                            onClick={() => handleCopyText(customSender)}
                                        >
                                            복사
                                        </Button>
                                    </div>
                                </div>
                                <div className="flex border-b border-gray4 items-stretch w-full">
                                    <TableName className="">경조사어</TableName>
                                    {/* 명의 행과 같은 구조. 직접입력이면 입력창이 셀렉트 아래 줄로 붙는다 */}
                                    <div className="flex flex-col w-full min-w-0 m-[7px] gap-[7px]">
                                        <SelectBox
                                            height="max-h-64"
                                            disabled={level === 7}
                                            onSearchChange={value => setWordTxt(value)}
                                            searchable
                                            options={
                                                wordData?.data
                                                    ? [
                                                          { value: "custom", label: "직접입력" },
                                                          ...wordData.data.map(word => ({
                                                              value: word.word_idx.toString(),
                                                              label: word.wreath_word + "(" + word.search_word + ")",
                                                          })),
                                                      ]
                                                    : [{ value: "custom", label: "직접입력" }]
                                            }
                                            full
                                            className="w-full [&_span]:whitespace-normal [&_span]:break-words [&_span]:min-w-0"
                                            value={word.toString()}
                                            onChange={value => {
                                                if (value === "custom") {
                                                    setWord(value);
                                                    setCustomWord("");
                                                } else {
                                                    const selectedWord = wordData?.data.find(word => word.word_idx.toString() === value);
                                                    if (selectedWord) {
                                                        setWord(value);
                                                        setCustomWord(selectedWord.wreath_word);
                                                    }
                                                }
                                            }}
                                        />

                                        {word === "custom" && (
                                            <Input
                                                status={level === 7 ? "view" : undefined}
                                                className="w-full min-w-0"
                                                value={customWord ?? ""}
                                                onChange={e => setCustomWord(e.target.value)}
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center shrink-0 px-[7px]">
                                        <Button
                                            icon={<CopyIcon />}
                                            iconPosition="right"
                                            size="S"
                                            className="px-2"
                                            onClick={() => handleCopyText(customWord)}
                                        >
                                            복사
                                        </Button>
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex items-center border-b border-gray4 w-1/2">
                                        <TableName className="h-12" required>
                                            받으시는 분
                                        </TableName>
                                        <Input
                                            placeholder="40자"
                                            maxLength={40}
                                            status={receiverError ? "error" : level === 7 ? "view" : undefined}
                                            className="px-2"
                                            value={receiver ?? ""}
                                            onChange={e => {
                                                setReceiver(e.target.value);
                                                setReceiverError(false);
                                            }}
                                        />
                                    </div>
                                    <div className="flex items-center border-b border-gray4 w-1/2">
                                        <TableName className="h-12" required>
                                            연락처
                                        </TableName>
                                        <Input
                                            status={receiverMobileError ? "error" : level === 7 ? "view" : undefined}
                                            className="px-2"
                                            value={receiverMobile ?? ""}
                                            onChange={e => {
                                                setReceiverMobileError(false);
                                                handlePhoneNumberChange("recipient_mobile", e.target.value);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="flex border-b border-gray4">
                                    <div className="flex w-1/2">
                                        <TableName className="h-12" required>
                                            경조내용
                                        </TableName>
                                        <SelectBox
                                            disabled={level === 7}
                                            options={
                                                eventData?.data
                                                    ? [
                                                          ...eventData.data.map(event => ({
                                                              value: event.depth1_idx.toString(),
                                                              label: event.depth1,
                                                          })),
                                                      ]
                                                    : []
                                            }
                                            full
                                            className="w-full m-[7px]"
                                            value={event.toString()}
                                            onChange={handleEventChange}
                                        />
                                    </div>
                                    {event === "2" || event === 2 ? (
                                        <Input
                                            className="m-[7px] w-1/2"
                                            value={eventContent}
                                            onChange={e => setEventContent(e.target.value)}
                                        />
                                    ) : (
                                        <SelectBox
                                            height="max-h-64"
                                            disabled={level === 7 || event === "1" || event === 1}
                                            disabledBG={event === "1" || event === 1}
                                            options={
                                                event2Data?.data
                                                    ? [
                                                          ...event2Data.data.map(event => ({
                                                              value: event.event_content_idx.toString() || "",
                                                              label: event.depth2 || "",
                                                          })),
                                                      ]
                                                    : []
                                            }
                                            full
                                            className="w-1/2 m-[7px]"
                                            value={event2.toString()}
                                            onChange={value => {
                                                setEvent2(value);
                                            }}
                                        />
                                    )}
                                </div>
                                <div className="flex border-b border-gray4">
                                    <TableName className="h-12">대상자명</TableName>
                                    <Input
                                        status={level === 7 ? "view" : undefined}
                                        className="p-2 w-full"
                                        value={related ?? ""}
                                        onChange={e => setRelated(e.target.value)}
                                    />
                                </div>
                                <div className="flex border-b border-gray4">
                                    <TableName className="h-12" required>
                                        즉시배송
                                    </TableName>
                                    <div className="px-2 flex items-center gap-1">
                                        <Toggle
                                            disabled={level === 7}
                                            onLabel="즉시배송 활성화"
                                            isOn={immediate}
                                            // onToggle={() => {
                                            //     setImmediate(!immediate);
                                            //      setSelectedDate(prevDate =>
                                            //         !immediate ? (data?.order_date ? new Date(data?.order_date) : undefined) : prevDate,
                                            //     );
                                            // }}
                                            onToggle={() => {
                                                setImmediate(!immediate);
                                                setSelectedDate(prevDate => (!immediate ? today : prevDate));
                                            }}
                                            offLabel="즉시배송 비활성화"
                                        />
                                        {/*<p className="text-sm text-grayicon">(접수시간 19:46)</p>*/}
                                    </div>
                                </div>
                                <div className="flex items-center border-b border-gray4">
                                    <TableName className="h-12" required>
                                        배송일자
                                    </TableName>
                                    <div className="h-12 w-full p-2">
                                        <DayPickerInput
                                            disabled={level === 7 || immediate}
                                            initialDate={selectedDate}
                                            onDateChange={date => {
                                                if (date) {
                                                    const adjustedDate = new Date(date);
                                                    adjustedDate.setHours(0, 0, 0, 0);
                                                    setSelectedDate(adjustedDate);
                                                } else {
                                                    setSelectedDate(undefined);
                                                }
                                            }}
                                            className="border border-gray4 h-full rounded-lg px-2"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center border-b border-gray4">
                                    <TableName className="h-12" required>
                                        배송시간
                                    </TableName>
                                    <div className="flex gap-1 w-full p-2">
                                        <SelectBox
                                            height="max-h-52"
                                            disabledBG={immediate}
                                            status={timeError ? "error" : ""}
                                            placeholder="시 선택"
                                            className="w-full"
                                            disabled={immediate || level === 7}
                                            options={hoursOptions}
                                            value={immediate ? "" : deliveryHour}
                                            onChange={value => {
                                                setTimeError(false);
                                                setDeliveryHour(value);

                                                if (textOnlyHourOptions.includes(value)) {
                                                    setDeliveryMinute("");
                                                    setDeliveryCondition("");
                                                }
                                            }}
                                        />
                                        <SelectBox
                                            disabledBG={immediate || isTextOnlyHour}
                                            status={timeError ? "error" : ""}
                                            placeholder="분 선택"
                                            disabled={immediate || level === 7 || isTextOnlyHour}
                                            className="w-full"
                                            options={minutesOptions}
                                            value={immediate ? "" : deliveryMinute}
                                            onChange={value => {
                                                setTimeError(false);
                                                setDeliveryMinute(value);
                                            }}
                                        />
                                        <SelectBox
                                            disabledBG={immediate || isTextOnlyHour}
                                            status={timeError ? "error" : ""}
                                            disabled={immediate || level === 7 || isTextOnlyHour}
                                            className="w-full"
                                            options={conditionOptions}
                                            value={immediate ? "" : deliveryCondition}
                                            onChange={value => {
                                                setTimeError(false);
                                                setDeliveryCondition(value);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="flex border-b border-gray4 items-stretch">
                                    <TableName className="" required>
                                        배송지 주소
                                    </TableName>
                                    <NewOrderSearchAddress
                                        status={level === 7 ? "view" : undefined}
                                        post={zoneCode}
                                        customerAddress={address}
                                        customerAddressDetail={detailAddress}
                                        onAddressChange={handleAddressChange}
                                    />
                                </div>
                                <div className="flex border-b border-gray4">
                                    <div className={`flex min-w-0 ${memo === "1" ? "w-1/2" : "w-full"}`}>
                                        <TableName className="h-12">
                                            인수증 <br />
                                            배송 메모
                                        </TableName>
                                        <SelectBox
                                            height="max-h-36"
                                            disabled={level === 7}
                                            options={
                                                memoData?.data && [
                                                    ...memoData.data.map(memo => ({
                                                        value: memo.delivery_memo_idx.toString(),
                                                        label: memo.delivery_memo,
                                                    })),
                                                ]
                                            }
                                            full
                                            className="w-full m-[7px]"
                                            value={memo.toString()}
                                            onChange={value => {
                                                setMemo(value);
                                                if (value !== "1") {
                                                    const selectedMemo = memoData?.data.find(
                                                        memo => memo.delivery_memo_idx.toString() === value,
                                                    );
                                                    if (selectedMemo) {
                                                        setCustomMemo(selectedMemo.delivery_memo);
                                                    }
                                                } else {
                                                    setCustomMemo("");
                                                }
                                            }}
                                        />
                                    </div>
                                    {memo === "1" && (
                                        <Input
                                            status={level === 7 ? "view" : undefined}
                                            className="m-[7px] w-1/2"
                                            value={customMemo ?? ""}
                                            onChange={e => setCustomMemo(e.target.value)}
                                        />
                                    )}
                                </div>
                            
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {level !== 7 && (
                <div className="flex rounded-b-xl w-full gap-2 pt-3 sticky pb-3 px-3 bottom-0 bg-white z-50">
                    <Button className="w-full" size="L" onClick={handleResetToInitialData}>
                        취소
                    </Button>
                    <Button
                        status="regist"
                        className="w-full"
                        size="L"
                        onClick={() => {
                            handleSubmit();
                        }}
                    >
                        저장
                    </Button>
                </div>
            )}

            {previewImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50"
                    onClick={() => setPreviewImage(null)}
                >
                    <div className="w-[920px] bg-white rounded-2xl shadow-custom">
                        <div className="flex items-center justify-between px-6 py-5">
                            <p className="text-2xl font-bold">이미지 크게 보기</p>
                            <CloseBlackIcon className="cursor-pointer" onClick={() => setPreviewImage(null)} />
                        </div>
                        <div className="flex p-5  items-center justify-center ">
                            <img src={previewImage} alt="preview" className="object-contain max-h-[500px]" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BranchOrderDetail;
